import React, { useMemo } from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  buildScoreRadarFromReport,
  buildRadar7AxesFromReport,
  buildRadar7AxesFromDimensions,
} from "../util/ScoreRadarUtils";

export default function StockRadarChart(props) {
  const colors = useMemo(
    () => ({
      labelColor: props.darkBackground ? "#ffffff" : "#333333",
      labelColorLight: props.darkBackground ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.6)",
      gridColor: props.darkBackground ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
      strokeColor: props.darkBackground ? "#fff" : "#333",
    }),
    [props.darkBackground]
  );

  const gradients = {
    good: { start: "#52D017", end: "#7CFC00" },
    neutral: { start: "#FFE562", end: "#FFD700" },
    bad: { start: "#DA110D", end: "#FF0000" },
  };

  const getFillColor = (recommendation) => {
    if (
      recommendation === "Good-Fundamentals" ||
      recommendation === "Strong-Fundamentals"
    ) {
      return "url(#goodGradient)";
    }
    if (recommendation === "Neutral-Fundamentals") {
      return "url(#neutralGradient)";
    }
    return "url(#badGradient)";
  };

  const getFontSize = () => {
    if (props.labelSize === "long") return 11;
    return 8;
  };

  const fontSize = getFontSize();
  const maxLabelWidth = props.labelSize === "long" ? 58 : 38;
  const labelHeight = fontSize * 3;
  const labelOffset = 1.2; // push labels outward from chart so they don't overlap the polygon

  // Custom tick: wrap text at word boundaries and place label outside the radar polygon
  const renderWrappedTick = (tickProps) => {
    const { payload, x, y, cx = 0, cy = 0 } = tickProps;
    const text = payload?.value ?? "";
    const dx = x - cx;
    const dy = y - cy;
    const labelX = cx + dx * labelOffset;
    const labelY = cy + dy * labelOffset;
    return (
      <g transform={`translate(${labelX},${labelY})`}>
        <foreignObject
          x={-maxLabelWidth / 2}
          y={-labelHeight / 2}
          width={maxLabelWidth}
          height={labelHeight}
          style={{ overflow: "visible" }}
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: "100%",
              minHeight: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflowWrap: "break-word",
              wordBreak: "normal",
              textAlign: "center",
              fontSize: `${fontSize}px`,
              lineHeight: 1.25,
              fontWeight: "600",
              color: colors.labelColor,
              boxSizing: "border-box",
              padding: "0 2px",
            }}
          >
            {text}
          </div>
        </foreignObject>
      </g>
    );
  };

  // Use scoreRadar from API when provided; else build from scoreData for recommendation/total
  const radarPayload = useMemo(() => {
    const scoreRadar = props.scoreRadar;
    const scoreData = props.scoreData;
    if (scoreRadar?.categories?.length >= 4) {
      return scoreRadar;
    }
    return buildScoreRadarFromReport(scoreData);
  }, [props.scoreRadar, props.scoreData]);

  // 7 axes: Sales Growth, Earnings Growth, ROE, ROIC, Fair Value, PEG, D/E
  const chartData = useMemo(() => {
    const scoreRadar = props.scoreRadar;
    const scoreData = props.scoreData;
    if (scoreRadar?.radarDimensions?.length >= 7) {
      const axes = buildRadar7AxesFromDimensions(scoreRadar.radarDimensions, true);
      return axes.map((a) => ({
        name: props.labelSize === "long" ? a.fullName : a.name,
        score: a.normalizedPercent,
        fullScore: 100,
        fullName: a.fullName,
        rawScore: a.rawScore,
        maxScore: a.maxScore,
      }));
    }
    if (scoreRadar?.dimensions?.length) {
      const axes = buildRadar7AxesFromDimensions(scoreRadar.dimensions);
      return axes.map((a) => ({
        name: props.labelSize === "long" ? a.fullName : a.name,
        score: a.normalizedPercent,
        fullScore: 100,
        fullName: a.fullName,
        rawScore: a.rawScore,
        maxScore: a.maxScore,
      }));
    }
    const axes = buildRadar7AxesFromReport(scoreData);
    return axes.map((a) => ({
      name: props.labelSize === "long" ? a.fullName : a.name,
      score: a.normalizedPercent,
      fullScore: 100,
      fullName: a.fullName,
      rawScore: a.rawScore,
      maxScore: a.maxScore,
    }));
  }, [props.scoreRadar, props.scoreData, props.labelSize]);

  const recommendation = radarPayload?.recommendation ?? props.scoreData?.recommendation ?? "";
  const totalScore = radarPayload?.totalScore ?? props.scoreData?.totalScore;
  const totalScoreMax = radarPayload?.totalScoreMax ?? 39;

  // Abbreviate recommendation for compact display to prevent truncation (e.g. "Strong-Fundamentals" → "Strong")
  const displayRecommendation =
    props.labelSize === "short" && recommendation
      ? recommendation.replace(/-Fundamentals$/, "")
      : recommendation;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: props.darkBackground ? "rgba(0,0,0,0.9)" : "rgba(255,255,255,0.95)",
            color: props.darkBackground ? "#fff" : "#333",
            padding: "8px 12px",
            borderRadius: "6px",
            border: `1px solid ${props.darkBackground ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}`,
            fontSize: "12px",
            fontWeight: "500",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontWeight: "700", marginBottom: "4px" }}>{data.fullName}</div>
          <div>
            {data.rawScore != null
              ? `Score: ${Number(data.rawScore).toFixed(1)} / ${data.maxScore ?? 3}`
              : `Score: ${data.score.toFixed(0)} / 100`}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "min(100%, 220px)",
          aspectRatio: "1",
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius="70%"
            data={chartData}
            margin={{ top: 28, right: 28, bottom: 28, left: 28 }}
          >
          <defs>
            <linearGradient id="goodGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={gradients.good.start} />
              <stop offset="100%" stopColor={gradients.good.end} />
            </linearGradient>
            <linearGradient id="neutralGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={gradients.neutral.start} />
              <stop offset="100%" stopColor={gradients.neutral.end} />
            </linearGradient>
            <linearGradient id="badGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={gradients.bad.start} />
              <stop offset="100%" stopColor={gradients.bad.end} />
            </linearGradient>
          </defs>
          <PolarGrid
            stroke={colors.gridColor}
            strokeDasharray="3 3"
            polarRadius={[25, 50, 75, 100]}
          />
          <PolarAngleAxis
            dataKey="name"
            tick={renderWrappedTick}
          />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
          <Tooltip content={<CustomTooltip />} />
          <Radar
            name={radarPayload?.symbol || props.scoreData?.symbol || ""}
            dataKey="score"
            stroke={colors.strokeColor}
            strokeWidth={2}
            fill={getFillColor(recommendation)}
            fillOpacity={0.7}
            animationBegin={0}
            animationDuration={1200}
            animationEasing="ease-out"
          />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {totalScore != null && (
        <div
          role={props.onScoreClick ? "button" : undefined}
          tabIndex={props.onScoreClick ? 0 : undefined}
          onKeyDown={props.onScoreClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onScoreClick(); } } : undefined}
          onClick={props.onScoreClick || undefined}
          style={{
            textAlign: "center",
            marginTop: "4px",
            fontSize: "11px",
            fontWeight: "600",
            color: props.darkBackground ? "rgba(255,255,255,0.9)" : "#333",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            ...(props.onScoreClick && {
              cursor: "pointer",
              textDecoration: "underline",
            }),
          }}
        >
          Score: {totalScore}/{totalScoreMax}
          {displayRecommendation ? ` · ${displayRecommendation}` : ""}
        </div>
      )}
    </div>
  );
}
